import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, Chip, CircularProgress, IconButton, Input, Sheet, Stack, Tooltip, Typography } from '@mui/joy'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PersonIcon from '@mui/icons-material/Person'
import TelegramIcon from '@mui/icons-material/Telegram'
import RefreshIcon from '@mui/icons-material/Refresh'
import LinkIcon from '@mui/icons-material/Link'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CheckIcon from '@mui/icons-material/Check'
import BusinessIcon from '@mui/icons-material/Business'
import SyncIcon from '@mui/icons-material/Sync'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

import {
    getTelegramCustomerChannels,
    listTelegramCustomerMessages,
    deleteTelegramCustomerMessage,
    requeueTelegramCustomerMessage,
    deleteTelegramCustomerChannel,
    type TelegramChannelHealth,
} from 'api/oracleApi'
import {
    oracleTgListAccounts,
    oracleTgStartWatcher,
    oracleTgStopWatcher,
    oracleTgLogout,
    oracleTgUpdateCustomerGroup,
    oracleTgGetCustomerBackfillStatus,
    oracleTgStartCustomerBackfill,
    oracleTgCancelCustomerBackfill,
    type OracleTgAccount,
    type OracleTgGroup,
} from 'api/oracleTelegramApi'
import { AccountsModal, ManageWatchedGroupsModal, CursorStrip, TelegramChannelDetailView, BackfillDialog } from '../ds-tg-dedicated/TelegramDedicatedPanel'

// ─── helpers ─────────────────────────────────────────────────────────────────

function relTime(iso: string | null): string {
    if (!iso) return '—'
    const ms = Date.now() - new Date(iso).getTime()
    if (!Number.isFinite(ms) || ms < 0) return '—'
    const sec = Math.floor(ms / 1000)
    if (sec < 60) return `${sec}s ago`
    const m = Math.floor(sec / 60)
    if (m < 60) return `${m}m ago`
    const h = Math.floor(m / 60)
    if (h < 24) return `${h}h ago`
    return `${Math.floor(h / 24)}d ago`
}

type JoyColor = 'neutral' | 'primary' | 'success' | 'warning' | 'danger'

function channelStatusColor(s: TelegramChannelHealth['status']): JoyColor {
    return s === 'active' ? 'success' : s === 'stale' ? 'warning' : s === 'paused' ? 'neutral' : 'danger'
}

function FactItem({ label, value, danger }: { label: string; value: string; danger?: boolean }): JSX.Element {
    return (
        <Stack direction="row" gap={0.5} alignItems="baseline">
            <Typography level="body-xs" color="neutral" sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {label}
            </Typography>
            <Typography level="body-xs" color={danger ? 'danger' : undefined} sx={{ fontWeight: 'md' }}>
                {value}
            </Typography>
        </Stack>
    )
}

// ─── assigned channel row ─────────────────────────────────────────────────────

function AssignedRow({
    channel,
    onReassign,
    onOpen,
}: {
    channel: TelegramChannelHealth
    onReassign: (peerId: string, name: string) => void
    onOpen: (peerId: string) => void
}): JSX.Element {
    const isActive = channel.status === 'active'
    return (
        <Sheet
            variant="outlined"
            onClick={() => onOpen(channel.peerId)}
            sx={{
                borderRadius: 'sm',
                px: 1.5,
                py: 1,
                borderLeftWidth: 3,
                borderLeftColor: `${channelStatusColor(channel.status)}.500`,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'background.level1' },
            }}
        >
            <Stack direction="row" alignItems="center" gap={1}>
                <TelegramIcon sx={{ fontSize: 16, color: isActive ? 'primary.500' : 'text.tertiary' }} />
                <Stack flex={1} minWidth={0} gap={0.25}>
                    <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                        <Typography level="body-sm" sx={{ fontWeight: 'md' }} noWrap>
                            {channel.name ?? channel.peerId}
                        </Typography>
                        <Chip size="sm" variant="soft" color={channelStatusColor(channel.status)} sx={{ fontSize: 10 }}>
                            {channel.status}
                        </Chip>
                        <Chip
                            size="sm"
                            variant="soft"
                            color="primary"
                            startDecorator={<LinkIcon sx={{ fontSize: 10 }} />}
                            onClick={(e) => {
                                e.stopPropagation()
                                onReassign(channel.peerId, channel.brokerCompanyName ?? '')
                            }}
                            sx={{ fontSize: 10, cursor: 'pointer' }}
                        >
                            {channel.brokerCompanyName}
                        </Chip>
                    </Stack>
                    <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
                        <FactItem label="pending" value={String(channel.counts.pending)} danger={channel.counts.pending > 10} />
                        <FactItem label="processed" value={String(channel.counts.processed)} />
                        {channel.counts.failed > 0 && <FactItem label="failed" value={String(channel.counts.failed)} danger />}
                        <FactItem label="last ingested" value={relTime(channel.lastIngestedAt)} />
                        <FactItem label="watchers" value={String(channel.watchingPhones.length)} />
                    </Stack>
                </Stack>
                <Typography level="h4" sx={{ color: 'text.tertiary', lineHeight: 1 }}>
                    ›
                </Typography>
            </Stack>
        </Sheet>
    )
}

// ─── unassigned group row ─────────────────────────────────────────────────────

function UnassignedRow({
    channel,
    onAssigned,
    onOpen,
}: {
    channel: TelegramChannelHealth
    onAssigned: () => void
    onOpen: (peerId: string) => void
}): JSX.Element {
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState('')
    const [saving, setSaving] = useState(false)
    const [err, setErr] = useState('')

    const save = async () => {
        const name = draft.trim()
        if (!name) return
        setSaving(true)
        try {
            await oracleTgUpdateCustomerGroup(channel.peerId, { brokerCompanyName: name })
            onAssigned()
        } catch (e) {
            setErr((e as Error)?.message || String(e))
        } finally {
            setSaving(false)
        }
    }

    return (
        <Sheet
            variant="outlined"
            onClick={() => onOpen(channel.peerId)}
            sx={{
                borderRadius: 'sm',
                px: 1.5,
                py: 1,
                borderLeftWidth: 3,
                borderLeftColor: 'neutral.300',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'background.level1' },
            }}
        >
            <Stack direction="row" alignItems="center" gap={1}>
                <TelegramIcon sx={{ fontSize: 16, color: 'text.tertiary' }} />
                <Stack flex={1} minWidth={0} gap={0.5}>
                    <Typography level="body-sm" sx={{ fontWeight: 'md' }} noWrap>
                        {channel.name ?? channel.peerId}
                    </Typography>
                    {err && (
                        <Typography level="body-xs" color="danger">
                            {err}
                        </Typography>
                    )}
                    {editing ? (
                        <Stack direction="row" gap={0.75} alignItems="center" onClick={(e) => e.stopPropagation()}>
                            <Input
                                size="sm"
                                autoFocus
                                placeholder="Company name, e.g. JB Hunt"
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') void save()
                                    if (e.key === 'Escape') setEditing(false)
                                }}
                                sx={{ width: 220 }}
                            />
                            <IconButton size="sm" variant="solid" color="primary" onClick={() => void save()} loading={saving} disabled={!draft.trim()}>
                                <CheckIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            <Button size="sm" variant="plain" color="neutral" onClick={() => setEditing(false)}>
                                Cancel
                            </Button>
                        </Stack>
                    ) : (
                        <Button
                            size="sm"
                            variant="soft"
                            color="neutral"
                            startDecorator={<BusinessIcon sx={{ fontSize: 13 }} />}
                            onClick={(e) => {
                                e.stopPropagation()
                                setEditing(true)
                                setDraft('')
                            }}
                            sx={{ alignSelf: 'flex-start', fontSize: 11 }}
                        >
                            Assign company
                        </Button>
                    )}
                </Stack>
                <Stack alignItems="flex-end" gap={0.25}>
                    <Typography level="body-xs" color="neutral">
                        {channel.watchingPhones.length} watcher{channel.watchingPhones.length !== 1 ? 's' : ''}
                    </Typography>
                    <Chip size="sm" variant="soft" color="neutral" sx={{ fontSize: 10 }}>
                        not processing
                    </Chip>
                </Stack>
                <Typography level="h4" sx={{ color: 'text.tertiary', lineHeight: 1 }}>
                    ›
                </Typography>
            </Stack>
        </Sheet>
    )
}

// ─── reassign inline (used when clicking the company chip on an assigned row) ─

function ReassignModal({ peerId, current, onDone }: { peerId: string; current: string; onDone: () => void }): JSX.Element {
    const [draft, setDraft] = useState(current)
    const [saving, setSaving] = useState(false)

    const save = async () => {
        setSaving(true)
        try {
            await oracleTgUpdateCustomerGroup(peerId, { brokerCompanyName: draft.trim() || null })
            onDone()
        } finally {
            setSaving(false)
        }
    }

    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 1.25, bgcolor: 'background.level1' }}>
            <Stack direction="row" gap={0.75} alignItems="center">
                <Input
                    size="sm"
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') void save()
                        if (e.key === 'Escape') onDone()
                    }}
                    placeholder="Company name"
                    sx={{ width: 200 }}
                />
                <Button size="sm" variant="solid" color="primary" onClick={() => void save()} loading={saving}>
                    Save
                </Button>
                <Button size="sm" variant="plain" color="neutral" onClick={onDone}>
                    Cancel
                </Button>
            </Stack>
        </Sheet>
    )
}

// ─── customer channel detail wrapper ─────────────────────────────────────────

function CustomerChannelDetailView({ channel, onBack, onRefresh }: { channel: TelegramChannelHealth; onBack: () => void; onRefresh: () => void }): JSX.Element {
    const [backfillOpen, setBackfillOpen] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [deleteError, setDeleteError] = useState('')
    const [messageReloadKey, setMessageReloadKey] = useState(0)

    const handleDeleteChannel = async () => {
        const label = channel.name || channel.peerId
        const msg = `Delete channel "${label}"?\n\nThis permanently removes:\n· all messages for this channel\n· watch subscriptions\n· dedup state\n· the group row itself\n\nThis cannot be undone.`
        if (!window.confirm(msg)) return
        setDeleting(true)
        try {
            const result = await deleteTelegramCustomerChannel(channel.peerId)
            window.alert(
                `Deleted ${result.messages} messages, ${result.watches} watches, ${result.processed} dedup records${result.groupRowDeleted ? ', and the group row' : ''}.`
            )
            onBack()
            onRefresh()
        } catch (err) {
            setDeleteError(`Delete failed: ${(err as Error)?.message || String(err)}`)
        } finally {
            setDeleting(false)
        }
    }

    const backfillPhone = channel.watchingPhones[0] ?? null
    const groupForBackfill = useMemo<OracleTgGroup>(
        () => ({
            peerId: channel.peerId,
            originalName: channel.name,
            brokerCompanyName: channel.brokerCompanyName,
            joinLink: null,
            status: 'active',
            createdAt: '',
            updatedAt: '',
        }),
        [channel]
    )

    const handleBackfillComplete = useCallback(() => {
        setBackfillOpen(false)
        setMessageReloadKey((k) => k + 1)
        onRefresh()
    }, [onRefresh])

    return (
        <Stack gap={1}>
            {deleteError && (
                <Alert
                    color="danger"
                    size="sm"
                    endDecorator={
                        <IconButton size="sm" variant="plain" onClick={() => setDeleteError('')}>
                            ×
                        </IconButton>
                    }
                >
                    {deleteError}
                </Alert>
            )}
            <TelegramChannelDetailView
                key={messageReloadKey}
                channel={channel}
                onBack={onBack}
                onRefresh={onRefresh}
                listMessages={listTelegramCustomerMessages}
                requeueMessage={requeueTelegramCustomerMessage}
                deleteMessage={deleteTelegramCustomerMessage}
                headerActions={
                    <>
                        <Tooltip title={backfillPhone ? `Backfill via ${backfillPhone}` : 'No watching account'} size="sm">
                            <span>
                                <Button
                                    size="sm"
                                    variant="outlined"
                                    startDecorator={<SyncIcon sx={{ fontSize: 14 }} />}
                                    onClick={() => setBackfillOpen(true)}
                                    disabled={!backfillPhone}
                                >
                                    Backfill
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title="Delete channel and all its data" size="sm">
                            <Button
                                size="sm"
                                variant="outlined"
                                color="danger"
                                startDecorator={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                                onClick={() => void handleDeleteChannel()}
                                loading={deleting}
                            >
                                Delete
                            </Button>
                        </Tooltip>
                    </>
                }
            />
            {backfillOpen && backfillPhone && (
                <BackfillDialog
                    phone={backfillPhone}
                    group={groupForBackfill}
                    onClose={() => setBackfillOpen(false)}
                    onComplete={handleBackfillComplete}
                    getBackfillStatus={oracleTgGetCustomerBackfillStatus}
                    startBackfillFn={oracleTgStartCustomerBackfill}
                    cancelBackfillFn={oracleTgCancelCustomerBackfill}
                />
            )}
        </Stack>
    )
}

// ─── main panel ──────────────────────────────────────────────────────────────

export function TelegramCustomerChannelsPanel({ onBack }: { onBack: () => void }): JSX.Element {
    const [channels, setChannels] = useState<TelegramChannelHealth[]>([])
    const [accounts, setAccounts] = useState<OracleTgAccount[]>([])
    const [accountsLoading, setAccountsLoading] = useState(false)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [accountsOpen, setAccountsOpen] = useState(false)
    const [manageFor, setManageFor] = useState<OracleTgAccount | null>(null)
    const [reassigning, setReassigning] = useState<{ peerId: string; current: string } | null>(null)
    const [selectedPeerId, setSelectedPeerId] = useState<string | null>(null)

    const reload = useCallback(async () => {
        try {
            setChannels(await getTelegramCustomerChannels())
            setError(null)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load channels')
        } finally {
            setLoading(false)
        }
    }, [])

    const loadAccounts = useCallback(async () => {
        setAccountsLoading(true)
        try {
            setAccounts(await oracleTgListAccounts())
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load accounts')
        } finally {
            setAccountsLoading(false)
        }
    }, [])

    useEffect(() => {
        void reload()
        void loadAccounts()
    }, [reload, loadAccounts])

    const handleStart = useCallback(
        async (phone: string) => {
            await oracleTgStartWatcher(phone)
            await loadAccounts()
        },
        [loadAccounts]
    )
    const handleStop = useCallback(
        async (phone: string) => {
            await oracleTgStopWatcher(phone)
            await loadAccounts()
        },
        [loadAccounts]
    )
    const handleLogout = useCallback(
        async (phone: string) => {
            await oracleTgLogout(phone)
            await loadAccounts()
        },
        [loadAccounts]
    )

    const assigned = useMemo(() => channels.filter((c) => c.brokerCompanyName), [channels])
    const unassigned = useMemo(() => channels.filter((c) => !c.brokerCompanyName), [channels])

    const aggregate = useMemo(() => {
        const a = { pending: 0, processed: 0, unattributed: 0, failed: 0 }
        for (const c of assigned) {
            a.pending += c.counts.pending
            a.processed += c.counts.processed
            a.unattributed += c.counts.unattributed
            a.failed += c.counts.failed
        }
        return a
    }, [assigned])

    const selectedChannel = useMemo(() => channels.find((c) => c.peerId === selectedPeerId) ?? null, [channels, selectedPeerId])

    return (
        <Stack gap={1.5}>
            <Stack direction="row" alignItems="center" gap={1}>
                <IconButton size="sm" variant="plain" onClick={onBack}>
                    <ArrowBackIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <TelegramIcon sx={{ fontSize: 18, color: 'primary.500' }} />
                <Typography level="title-sm" sx={{ flex: 1 }}>
                    Customer Telegram Channels
                </Typography>
                <Tooltip title="Refresh" size="sm">
                    <IconButton size="sm" variant="outlined" onClick={reload} loading={loading}>
                        <RefreshIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
                <Button
                    size="sm"
                    variant="outlined"
                    color="primary"
                    startDecorator={<PersonIcon sx={{ fontSize: 14 }} />}
                    onClick={() => setAccountsOpen(true)}
                >
                    Accounts {accounts.length > 0 ? `· ${accounts.length}` : ''}
                </Button>
            </Stack>

            {error && (
                <Alert
                    size="sm"
                    color="danger"
                    startDecorator={<ErrorOutlineIcon />}
                    endDecorator={
                        <IconButton size="sm" variant="plain" color="danger" onClick={() => setError(null)}>
                            ×
                        </IconButton>
                    }
                >
                    {error}
                </Alert>
            )}

            {selectedChannel ? (
                <CustomerChannelDetailView channel={selectedChannel} onBack={() => setSelectedPeerId(null)} onRefresh={() => void reload()} />
            ) : loading ? (
                <Stack alignItems="center" py={4}>
                    <CircularProgress size="sm" />
                </Stack>
            ) : channels.length === 0 ? (
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 3, textAlign: 'center' }}>
                    <Stack alignItems="center" gap={1}>
                        <TelegramIcon sx={{ fontSize: 28, color: 'text.tertiary' }} />
                        <Typography level="body-sm" color="neutral">
                            No watched groups yet
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                            Open <strong>Accounts</strong>, manage a phone, and watch a Telegram group.
                        </Typography>
                    </Stack>
                </Sheet>
            ) : (
                <Stack gap={2}>
                    {/* Assigned section */}
                    {assigned.length > 0 && (
                        <Stack gap={0.75}>
                            <Stack direction="row" alignItems="center" gap={1}>
                                <Typography
                                    level="body-xs"
                                    sx={{ fontWeight: 'lg', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}
                                >
                                    Assigned · {assigned.length}
                                </Typography>
                                <Sheet variant="outlined" sx={{ borderRadius: 'xl', px: 1, py: 0.25 }}>
                                    <Stack direction="row" gap={0.75} alignItems="center">
                                        {aggregate.pending > 0 && (
                                            <Typography level="body-xs" color="warning" sx={{ fontWeight: 'md' }}>
                                                {aggregate.pending} pending
                                            </Typography>
                                        )}
                                        {aggregate.processed > 0 && (
                                            <Typography level="body-xs" color="success" sx={{ fontWeight: 'md' }}>
                                                {aggregate.processed} processed
                                            </Typography>
                                        )}
                                        {aggregate.failed > 0 && (
                                            <Typography level="body-xs" color="danger" sx={{ fontWeight: 'md' }}>
                                                {aggregate.failed} failed
                                            </Typography>
                                        )}
                                    </Stack>
                                </Sheet>
                            </Stack>
                            {assigned.map((c) =>
                                reassigning?.peerId === c.peerId ? (
                                    <Stack key={c.peerId} gap={0.5}>
                                        <AssignedRow
                                            channel={c}
                                            onReassign={(peerId, name) => setReassigning({ peerId, current: name })}
                                            onOpen={setSelectedPeerId}
                                        />
                                        <ReassignModal
                                            peerId={c.peerId}
                                            current={reassigning.current}
                                            onDone={() => {
                                                setReassigning(null)
                                                void reload()
                                            }}
                                        />
                                    </Stack>
                                ) : (
                                    <AssignedRow
                                        key={c.peerId}
                                        channel={c}
                                        onReassign={(peerId, name) => setReassigning({ peerId, current: name })}
                                        onOpen={setSelectedPeerId}
                                    />
                                )
                            )}
                        </Stack>
                    )}

                    {/* Unassigned section */}
                    {unassigned.length > 0 && (
                        <Stack gap={0.75}>
                            <Typography level="body-xs" sx={{ fontWeight: 'lg', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
                                Synced · {unassigned.length} — assign a company to start processing
                            </Typography>
                            {unassigned.map((c) => (
                                <UnassignedRow key={c.peerId} channel={c} onAssigned={() => void reload()} onOpen={setSelectedPeerId} />
                            ))}
                        </Stack>
                    )}
                </Stack>
            )}

            {accountsOpen && (
                <AccountsModal
                    accounts={accounts}
                    loading={accountsLoading}
                    onClose={() => setAccountsOpen(false)}
                    onAdded={() => void loadAccounts()}
                    onManage={(a) => {
                        setAccountsOpen(false)
                        setManageFor(a)
                    }}
                    onStart={handleStart}
                    onStop={handleStop}
                    onLogout={handleLogout}
                />
            )}
            {manageFor && (
                <ManageWatchedGroupsModal
                    account={manageFor}
                    mode="customer"
                    onClose={() => {
                        setManageFor(null)
                        void loadAccounts()
                        void reload()
                    }}
                />
            )}
        </Stack>
    )
}
